import React from 'react'
import ReactDOM from 'react-dom'
import { Provider} from 'react-redux'
import { createStore, applyMiddleware, compose } from 'redux'
import {createLogger} from 'redux-logger'
import reducers from './reducers/index'

import config from '../app.config'

import * as Sentry from '@sentry/browser'
import { CaptureConsole } from '@sentry/integrations'
import createSentryMiddleware from 'redux-sentry-middleware'

import userMiddleware from './middleware/user'
import browserMiddleware from './middleware/browser'
import currentUserMiddleware from './middleware/currentUser'
import settingsMiddleware from './middleware/settings'
import streamingMiddleware from './middleware/streaming'
import blockingMiddleware from './middleware/blocking'
import roomMessagesMiddleware from './middleware/roomMessages'
import pmSessionsMiddleware from './middleware/pmSessions'
import serviceMessages from './middleware/serviceMessages'
import instanotiMiddleware from './middleware/instanoti'
import metricsMiddleware from './middleware/metrics'
import roomsMiddleware from './middleware/rooms'
import firebaseMiddleware from './middleware/firebase'
import restartMiddleware from './middleware/restart.ts'
import visibilityEventSource from './middleware/visibilityMiddleware';

import sounds from './middleware/soundNotifications'

import pmMiddleware from 'pm-js/build/middleware/pmMiddleware.js'

import Registry from './registry/index';
import {loadLocale} from './locale/index';

import ChatInterface from './interfaces/chat/hitagi'
import reduxChatInterfaceBinding from './bindings/chatinterface-redux'
import blockBind from './bindings/chatinterface-block'

import {BobTVSiteInterface as SiteInterface} from './interfaces/site/index';

import NeoChat from './components/bobtv-neo/NeoChat'
import SentryErrorBoundary from './components/bobtv-neo/SentryErrorBoundary'

import {preRender, postRender} from './hooks';
import {listenInstallPrompt} from './homeScreen';

var browserDetection = require("imports-loader!./3rdparty/browser-detection.js");
browserDetection({addClasses: true});

import {initialState as browserInitialState, reduxBind as browserReduxBind} from './bindings/browser-redux';
import {MY_VIDEO_WINDOW_DEFAULT_STATE} from './constants/streaming';

import MediaConstraints from "./constraints.js"

import Settings from './settings/index';
let settings = new Settings();
Registry.add('settings', settings);

let initialState = {
    browser: browserInitialState,
    sidebars: {
        leftShown: !browserInitialState.mobile,
        rightShown: !browserInitialState.mobile,
    },
    myVideoWindow: {
        ...MY_VIDEO_WINDOW_DEFAULT_STATE,
        shown: browserInitialState.desktop
    },
    editorStyle: {
        fontWeight: settings.get('bold') ? 'bold' : 'normal',
        fontStyle: settings.get('italic') ? 'italic' : 'normal',
        color: settings.get('color'),
        fontSize: settings.get('size'),
        fontFamily: settings.get('family')
    },
    userSorting: settings.get('sortby')
};

import './sass/neo-chat.scss';
import {getCookie, getFingerprint} from "./utils";

let localeCode = document.location.pathname.includes('/en/') ? 'en' : 'fr';
let locale = loadLocale(localeCode);

var _debug = window.__debug || false;

var _hitagiDebug = window.__hitagiDebug || false;
var _loginData = window.__login_data || {};

_loginData.current_user = window.__currentUser || {};
var _constraints = window.chatNeoConstraints || {};
let constraints = new MediaConstraints(_constraints);

let cameraDeviceId = settings.get("media.camera");
if (cameraDeviceId) { constraints.set_device_id("video", cameraDeviceId); }

let resolution = settings.get("media.resolution");
if (resolution) {
    constraints.set_resolution(resolution);
}

let micDeviceId = settings.get("media.mic");
if (micDeviceId) { constraints.set_device_id("audio", micDeviceId); }

let voiceProcessing = settings.get("media.voice_processing");
if (voiceProcessing === "music") {
    constraints.enable_audio_processing(false);
} else {
    constraints.enable_audio_processing(true);
}

_constraints = constraints.get();

window.fingerprint = getCookie("BOBSID");

if(!window.fingerprint || window.fingerprint.length < 46) {
    getFingerprint();
}

let search_params = new URLSearchParams(document.location.hash.slice(1));
let u_param = search_params.get("u");
_loginData.ufrag = typeof u_param === 'string' ? atob(u_param) : null;


var chatInterface = new ChatInterface({..._loginData}, constraints, _hitagiDebug);

var siteInterface = new SiteInterface(locale.path);
if (!chatInterface.isAnonymous()) {
    siteInterface.setAuthParams({
        uid: chatInterface.getUid(),
        token: chatInterface.getToken()
    });
}

let chatDescriptionElement = document.getElementById("page-description");
if(chatDescriptionElement) {
    window.roomDescription = chatDescriptionElement.innerHTML;
    chatDescriptionElement.remove();
}

let reduxMiddleware = [
    userMiddleware,
    browserMiddleware,
    currentUserMiddleware,
    settingsMiddleware,
    blockingMiddleware, // important! blocking middleware should be called before before any other middleware it affects
    streamingMiddleware,
    roomMessagesMiddleware,
    pmSessionsMiddleware,

    pmMiddleware,

    serviceMessages,
    metricsMiddleware,
    roomsMiddleware,
    instanotiMiddleware,
    firebaseMiddleware,
    restartMiddleware,
    sounds
];

if (_debug === "redux") {
    reduxMiddleware.push(createLogger({
        duration: true,
        diff: true,
        predicate: (getState, action) => ['STREAM_WATCHER_COUNT', 'ROOM_LIST'].indexOf(action.type) == -1,
        logErrors: false
    }));
}

let sentryDsn = config.SENTRY_DSN;
let sentryRelease = config.SENTRY_RELEASE;

if (window.__runtimeAppConfig) {
    if (window.__runtimeAppConfig.SENTRY_DSN) sentryDsn = window.__runtimeAppConfig.SENTRY_DSN;
    if (window.__runtimeAppConfig.SENTRY_RELEASE) sentryRelease = window.__runtimeAppConfig.SENTRY_RELEASE;
}
if (sentryDsn) {
    Sentry.init({
        dsn: sentryDsn,
        release: sentryRelease,
        integrations: [
            new CaptureConsole({
                levels: ['error']
            })
        ],
    });
    reduxMiddleware.push(createSentryMiddleware(
        Sentry, { breadcrumbDataFromAction: action => ({ action }) }
    ))
}

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
var store = createStore(reducers, initialState, composeEnhancers( applyMiddleware(...reduxMiddleware)) );
reduxChatInterfaceBinding(store, chatInterface);
//reduxPmInterfaceBinding(store, chatInterface.pm);
blockBind(chatInterface, store);
browserReduxBind(store);


Registry.add({
    locale: locale,
    chatInterface: chatInterface,
    pmConfig: {
        url: _loginData.pm_server_uri,
        chatroom_name:  _loginData.chatroom_name
    },
    //pmInterface: pmInterface,
    siteInterface: siteInterface,
    store: store
});

visibilityEventSource(store);
preRender();

ReactDOM.render(
    <SentryErrorBoundary>
        <Provider store={store}>
            <NeoChat store={store}/>
        </Provider>
    </SentryErrorBoundary>,
    document.getElementById('root'),
    postRender
);

listenInstallPrompt();



// WEBPACK FOOTER //
// ./src/index.js